import { useState, useCallback, useEffect } from "react";
import { Navigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AppLayout } from "@/components/AppLayout";
import { DatePicker } from "@/components/ui/DatePicker";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, UserPlus, Clock, TrendingDown, BarChart3, Baby, Search, X, ShieldCheck, GraduationCap, Stethoscope, Laptop, CheckCircle2, AlertTriangle, ChevronRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  apiGetAnalyticsHeadcount,
  apiGetAnalyticsTurnover,
  apiGetAnalyticsLeaveSummary,
  apiGetAnalyticsPayrollSummary,
  apiGetAnalyticsDepartmentComparison,
  apiGetChildrenSummary,
  apiGetMedicalChecks,
  apiGetCertificationOverview,
  apiGetTrainingOverview,
  apiGetEquipment,
  apiGetEquipmentStock,
  apiGetTimekeepingReport,
  type ChildrenSummary,
} from "@/lib/api";
import { HeadcountChart } from "@/components/analytics/HeadcountChart";
import { DepartmentBreakdown } from "@/components/analytics/DepartmentBreakdown";
import { LeaveAnalytics } from "@/components/analytics/LeaveAnalytics";
import { PayrollTrend } from "@/components/analytics/PayrollTrend";
import { DepartmentComparison } from "@/components/analytics/DepartmentComparison";
import { TimekeepingAnalytics } from "@/components/analytics/TimekeepingAnalytics";
import { ReportCanvas } from "@/components/analytics/pivot/ReportCanvas";
import { ExportButton } from "@/components/analytics/ExportButton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function getDefaults() {
  const now = new Date();
  const to = now.toISOString().split("T")[0];
  const from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
  const yearStart = `${now.getFullYear()}-01-01`;
  return { from, to, yearStart };
}

interface KpiCardProps {
  icon: React.ReactNode;
  title: string;
  value: string | number;
  sub?: string;
  isLoading?: boolean;
}

function KpiCard({ icon, title, value, sub, isLoading }: KpiCardProps) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            {isLoading ? (
              <Skeleton className="h-8 w-20 mt-1" />
            ) : (
              <p className="text-3xl font-bold mt-1">{value}</p>
            )}
            {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
          </div>
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function HRAnalytics() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const defaults = getDefaults();
  const [dateFrom, setDateFrom] = useState(defaults.from);
  const [dateTo, setDateTo] = useState(defaults.to);
  const dateParams = { from: dateFrom, to: dateTo };

  const { data: headcount, isLoading: hcLoading } = useQuery({
    queryKey: ["analytics-headcount", dateFrom, dateTo],
    queryFn: () => apiGetAnalyticsHeadcount(dateParams),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  const { data: turnover, isLoading: toLoading } = useQuery({
    queryKey: ["analytics-turnover", dateFrom, dateTo],
    queryFn: () => apiGetAnalyticsTurnover(dateParams),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  const { data: leaveSummary, isLoading: leaveLoading } = useQuery({
    queryKey: ["analytics-leave", dateFrom, dateTo],
    queryFn: () => apiGetAnalyticsLeaveSummary(dateParams),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  const { data: payrollSummary, isLoading: payrollLoading } = useQuery({
    queryKey: ["analytics-payroll", dateFrom, dateTo],
    queryFn: () => apiGetAnalyticsPayrollSummary(dateParams),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  const { data: timekeepingReport, isLoading: tkLoading } = useQuery({
    queryKey: ["analytics-timekeeping", dateFrom, dateTo],
    queryFn: () => apiGetTimekeepingReport({ from: dateFrom, to: dateTo }),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  const { data: deptComparison, isLoading: deptLoading } = useQuery({
    queryKey: ["analytics-dept-comparison"],
    queryFn: apiGetAnalyticsDepartmentComparison,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  // Compliance & Equipment data
  const { data: medicalChecks, isLoading: medicalLoading } = useQuery({
    queryKey: ["medical-checks-analytics"],
    queryFn: apiGetMedicalChecks,
    staleTime: 5 * 60 * 1000,
  });
  const { data: certOverview = [], isLoading: certLoading } = useQuery({
    queryKey: ["cert-overview-analytics"],
    queryFn: apiGetCertificationOverview,
    staleTime: 5 * 60 * 1000,
  });
  const { data: trainingOverview = [], isLoading: trainingLoading } = useQuery({
    queryKey: ["training-overview-analytics"],
    queryFn: apiGetTrainingOverview,
    staleTime: 5 * 60 * 1000,
  });
  const { data: allEquipment = [], isLoading: equipLoading } = useQuery({
    queryKey: ["all-equipment-analytics"],
    queryFn: () => apiGetEquipment(),
    staleTime: 5 * 60 * 1000,
  });
  const { data: equipmentStock = [], isLoading: stockLoading } = useQuery({
    queryKey: ["equipment-stock-analytics"],
    queryFn: apiGetEquipmentStock,
    staleTime: 5 * 60 * 1000,
  });

  const [childrenMinAge, setChildrenMinAge] = useState<string>("");
  const [childrenMaxAge, setChildrenMaxAge] = useState<string>("");
  const [childrenSummary, setChildrenSummary] = useState<ChildrenSummary | null>(null);
  const [childrenSummaryLoading, setChildrenSummaryLoading] = useState(false);

  const loadChildrenSummary = useCallback(async (minAge?: number, maxAge?: number) => {
    try {
      setChildrenSummaryLoading(true);
      const data = await apiGetChildrenSummary(minAge, maxAge);
      setChildrenSummary(data);
    } catch { /* ignore */ } finally {
      setChildrenSummaryLoading(false);
    }
  }, []);

  useEffect(() => { loadChildrenSummary(); }, [loadChildrenSummary]);

  // Compliance derived values
  const nowDate = new Date();
  const in30Date = new Date(nowDate.getTime() + 30 * 86400000);

  const certTotal     = certOverview.length;
  const certCompliant = certOverview.filter(e => e.missingCerts.length === 0 && e.expiredCerts.length === 0 && e.expiringCerts.length === 0).length;
  const certExpiring  = certOverview.filter(e => e.expiringCerts.length > 0 && e.missingCerts.length === 0 && e.expiredCerts.length === 0).length;
  const certIssues    = certOverview.filter(e => e.missingCerts.length > 0 || e.expiredCerts.length > 0).length;
  const certRate      = certTotal > 0 ? Math.round((certCompliant / certTotal) * 100) : 0;

  const trainTotal     = trainingOverview.length;
  const trainCompliant = trainingOverview.filter(e => e.missingTrainings.length === 0 && e.expiredTrainings.length === 0 && e.expiringTrainings.length === 0).length;
  const trainExpiring  = trainingOverview.filter(e => e.expiringTrainings.length > 0 && e.missingTrainings.length === 0 && e.expiredTrainings.length === 0).length;
  const trainIssues    = trainingOverview.filter(e => e.missingTrainings.length > 0 || e.expiredTrainings.length > 0).length;
  const trainRate      = trainTotal > 0 ? Math.round((trainCompliant / trainTotal) * 100) : 0;

  const medValidRate = medicalChecks && medicalChecks.stats.total > 0
    ? Math.round((medicalChecks.stats.valid / medicalChecks.stats.total) * 100) : 0;

  // By-department breakdowns for certs and trainings
  const certByDept = Array.from(
    certOverview.reduce((m, e) => {
      const dept = e.department || "Unknown";
      if (!m.has(dept)) m.set(dept, { dept, total: 0, compliant: 0, issues: 0 });
      const r = m.get(dept)!;
      r.total++;
      if (e.missingCerts.length === 0 && e.expiredCerts.length === 0 && e.expiringCerts.length === 0) r.compliant++;
      else if (e.missingCerts.length > 0 || e.expiredCerts.length > 0) r.issues++;
      return m;
    }, new Map<string, { dept: string; total: number; compliant: number; issues: number }>())
  ).map(([, v]) => v).sort((a, b) => b.total - a.total);

  const trainByDept = Array.from(
    trainingOverview.reduce((m, e) => {
      const dept = e.department || "Unknown";
      if (!m.has(dept)) m.set(dept, { dept, total: 0, compliant: 0, issues: 0 });
      const r = m.get(dept)!;
      r.total++;
      if (e.missingTrainings.length === 0 && e.expiredTrainings.length === 0 && e.expiringTrainings.length === 0) r.compliant++;
      else if (e.missingTrainings.length > 0 || e.expiredTrainings.length > 0) r.issues++;
      return m;
    }, new Map<string, { dept: string; total: number; compliant: number; issues: number }>())
  ).map(([, v]) => v).sort((a, b) => b.total - a.total);

  // Equipment derived
  const equipAssigned   = allEquipment.filter(e => e.employeeId !== null).length;
  const equipUnassigned = allEquipment.filter(e => e.employeeId === null).length;
  const equipExpiring   = allEquipment.filter(e => {
    if (!e.expiryDate) return false;
    const d = new Date(e.expiryDate);
    return d >= nowDate && d <= in30Date;
  });
  const equipByCategory = Array.from(
    allEquipment.reduce((m, e) => {
      const cat = e.category || "Other";
      if (!m.has(cat)) m.set(cat, { cat, assigned: 0, total: 0 });
      const r = m.get(cat)!;
      r.total++;
      if (e.employeeId !== null) r.assigned++;
      return m;
    }, new Map<string, { cat: string; assigned: number; total: number }>())
  ).map(([, v]) => v).sort((a, b) => b.total - a.total);

  // Access guard — allow admin, hr, and c_level sub-role
  const isCLevel = user?.role.subRole === "c_level";
  if (!user || (user.role.mainRole !== "hr" && user.role.mainRole !== "admin" && !isCLevel)) {
    return <Navigate to="/" replace />;
  }

  // Flat data for CSV/Excel export
  const exportData: Record<string, any>[] = deptComparison?.departments.map((d) => ({
    Department: d.department,
    Headcount: d.headcount,
    Active: d.activeCount,
    "Avg Tenure (months)": d.avgTenureMonths.toFixed(1),
    "Leave Days YTD": d.totalLeaveDays,
    "Avg Gross Salary": d.avgGross.toFixed(0),
  })) ?? [];

  const handleThisYear = () => {
    setDateFrom(defaults.yearStart);
    setDateTo(defaults.to);
  };

  return (
    <AppLayout>
      <style>{`@media print { .no-print { display: none !important; } }`}</style>

      <div id="analytics-printable" className="space-y-6 p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-indigo-500/10 flex items-center justify-center shrink-0">
              <BarChart3 className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">{t("analytics.title")}</h1>
              <p className="text-muted-foreground text-sm mt-1">{t("analytics.subtitle")}</p>
            </div>
          </div>
        </div>

        <Tabs defaultValue="dashboard" className="space-y-6">
          <TabsList className="no-print">
            <TabsTrigger value="dashboard">{t("analytics.pivot.tabDashboard")}</TabsTrigger>
            <TabsTrigger value="reports">{t("analytics.pivot.tabReportBuilder")}</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6 mt-0">
        <div className="flex justify-end no-print">
          <ExportButton data={exportData} filename="hr-analytics" />
        </div>

        {/* Date range filter */}
        <div className="flex flex-wrap items-center gap-3 p-4 rounded-lg border bg-card no-print">
          <span className="text-sm font-medium">{t("analytics.dateRange")}:</span>
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground">{t("analytics.from")}</label>
            <DatePicker value={dateFrom} onChange={setDateFrom} className="h-8 w-[140px] text-sm" />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground">{t("analytics.to")}</label>
            <DatePicker value={dateTo} onChange={setDateTo} className="h-8 w-[140px] text-sm" />
          </div>
          <Button variant="outline" size="sm" onClick={handleThisYear}>
            {t("analytics.thisYear")}
          </Button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            icon={<Users className="h-5 w-5" />}
            title={t("analytics.kpiHeadcount")}
            value={headcount?.total ?? "—"}
            sub={`${headcount?.active ?? 0} active`}
            isLoading={hcLoading}
          />
          <KpiCard
            icon={<UserPlus className="h-5 w-5" />}
            title={t("analytics.kpiNewHires")}
            value={headcount?.newHiresThisMonth ?? "—"}
            sub="this month"
            isLoading={hcLoading}
          />
          <KpiCard
            icon={<Clock className="h-5 w-5" />}
            title={t("analytics.kpiAvgTenure")}
            value={
              headcount
                ? `${headcount.avgTenureMonths.toFixed(1)} ${t("analytics.months")}`
                : "—"
            }
            isLoading={hcLoading}
          />
          <KpiCard
            icon={<TrendingDown className="h-5 w-5" />}
            title={t("analytics.kpiTurnoverRate")}
            value={turnover ? `${turnover.avgTurnoverRate}%` : "—"}
            sub={`${turnover?.totalTerminations ?? 0} terminations`}
            isLoading={toLoading}
          />
        </div>

        {/* Headcount Over Time */}
        <HeadcountChart data={headcount?.monthly ?? []} isLoading={hcLoading} />

        {/* Department Breakdown */}
        <DepartmentBreakdown
          deptData={headcount?.byDepartment ?? []}
          contractData={headcount?.byContractType ?? []}
          genderData={headcount?.byGender ?? []}
          isLoading={hcLoading}
        />

        {/* Leave Analytics */}
        <LeaveAnalytics data={leaveSummary} isLoading={leaveLoading} />

        {/* Payroll Trend */}
        <PayrollTrend data={payrollSummary?.monthly ?? []} isLoading={payrollLoading} />

        {/* Timekeeping */}
        <TimekeepingAnalytics data={timekeepingReport} isLoading={tkLoading} />

        {/* Department Comparison Table */}
        <DepartmentComparison
          data={deptComparison?.departments ?? []}
          isLoading={deptLoading}
        />

        {/* ── Compliance Overview ───────────────────────────────────────────── */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-foreground">Compliance Overview</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

            {/* Medical Checkups */}
            <Card>
              <CardHeader className="pb-3 border-b">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <div className="h-7 w-7 rounded-lg bg-teal-500/10 flex items-center justify-center shrink-0">
                    <Stethoscope className="h-3.5 w-3.5 text-teal-600" />
                  </div>
                  Medical Checkups
                  <Link to="/medical-checkups" className="ml-auto text-xs font-normal text-muted-foreground hover:text-foreground flex items-center gap-0.5 transition-colors">
                    View all <ChevronRight className="h-3 w-3" />
                  </Link>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                {medicalLoading ? (
                  <div className="space-y-2"><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-3/4" /></div>
                ) : medicalChecks ? (
                  <>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-muted-foreground">Validity rate</span>
                      <span className="text-sm font-semibold tabular-nums">{medValidRate}%</span>
                    </div>
                    <Progress value={medValidRate} className="h-2" />
                    <div className="grid grid-cols-2 gap-2 pt-1">
                      {[
                        { label: t("medicalCheckups.stats.valid"),        value: medicalChecks.stats.valid,    color: "text-emerald-600" },
                        { label: t("medicalCheckups.stats.dueSoon"),      value: medicalChecks.stats.due_soon, color: "text-amber-600"  },
                        { label: t("medicalCheckups.stats.expired"),      value: medicalChecks.stats.expired,  color: "text-red-600"    },
                        { label: t("medicalCheckups.stats.failed"),       value: medicalChecks.stats.failed,   color: "text-rose-600"   },
                        { label: t("medicalCheckups.stats.noExam"),       value: medicalChecks.stats.no_exam,  color: "text-slate-600"  },
                        { label: t("medicalCheckups.stats.totalTracked"), value: medicalChecks.stats.total,    color: "text-blue-600"   },
                      ].map(({ label, value, color }) => (
                        <div key={label} className="flex items-center justify-between text-xs py-1 px-2 rounded-md bg-muted/40">
                          <span className="text-muted-foreground">{label}</span>
                          <span className={`font-semibold tabular-nums ${color}`}>{value}</span>
                        </div>
                      ))}
                    </div>
                    {medicalChecks.employees.filter(e => e.status !== "valid").length > 0 && (
                      <div className="pt-1 border-t border-border space-y-1.5">
                        <p className="text-xs font-medium text-muted-foreground">Needs attention</p>
                        {medicalChecks.employees.filter(e => e.status !== "valid").slice(0, 5).map(emp => (
                          <div key={emp.employeeId} className="flex items-center justify-between text-xs">
                            <span className="font-medium truncate max-w-[140px]">{emp.employeeName}</span>
                            <Badge variant="outline" className={`text-[10px] shrink-0 ${emp.status === "expired" ? "bg-red-50 text-red-700 border-red-200" : emp.status === "due_soon" ? "bg-amber-50 text-amber-700 border-amber-200" : emp.status === "failed" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-slate-50 text-slate-700 border-slate-200"}`}>
                              {t(`medicalCheckups.status.${emp.status}`)}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No data available</p>
                )}
              </CardContent>
            </Card>

            {/* Certifications */}
            <Card>
              <CardHeader className="pb-3 border-b">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <div className="h-7 w-7 rounded-lg bg-violet-500/10 flex items-center justify-center shrink-0">
                    <ShieldCheck className="h-3.5 w-3.5 text-violet-600" />
                  </div>
                  Certifications
                  <Link to="/certifications" className="ml-auto text-xs font-normal text-muted-foreground hover:text-foreground flex items-center gap-0.5 transition-colors">
                    View all <ChevronRight className="h-3 w-3" />
                  </Link>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                {certLoading ? (
                  <div className="space-y-2"><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-3/4" /></div>
                ) : certTotal === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No position requirements configured</p>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-muted-foreground">Compliance rate</span>
                      <span className="text-sm font-semibold tabular-nums">{certRate}%</span>
                    </div>
                    <Progress value={certRate} className="h-2" />
                    <div className="grid grid-cols-3 gap-2 pt-1">
                      {[
                        { label: "Compliant", value: certCompliant, color: "text-emerald-600" },
                        { label: "Expiring",  value: certExpiring,  color: "text-amber-600"  },
                        { label: "Issues",    value: certIssues,    color: "text-red-600"    },
                      ].map(({ label, value, color }) => (
                        <div key={label} className="text-center rounded-md bg-muted/40 py-2 px-1">
                          <p className={`text-xl font-bold tabular-nums ${color}`}>{value}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
                        </div>
                      ))}
                    </div>
                    {certByDept.length > 0 && (
                      <div className="pt-1 border-t border-border space-y-2">
                        <p className="text-xs font-medium text-muted-foreground">By department</p>
                        {certByDept.slice(0, 5).map(d => {
                          const rate = d.total > 0 ? Math.round((d.compliant / d.total) * 100) : 0;
                          return (
                            <div key={d.dept} className="space-y-0.5">
                              <div className="flex justify-between text-xs">
                                <span className="text-muted-foreground truncate max-w-[130px]">{d.dept}</span>
                                <span className="font-medium tabular-nums shrink-0 ml-1">{rate}%</span>
                              </div>
                              <div className="h-1 rounded-full bg-muted overflow-hidden">
                                <div className="h-full rounded-full bg-violet-500/60" style={{ width: `${rate}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Trainings */}
            <Card>
              <CardHeader className="pb-3 border-b">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <div className="h-7 w-7 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                    <GraduationCap className="h-3.5 w-3.5 text-blue-600" />
                  </div>
                  Trainings
                  <Link to="/certifications" className="ml-auto text-xs font-normal text-muted-foreground hover:text-foreground flex items-center gap-0.5 transition-colors">
                    View all <ChevronRight className="h-3 w-3" />
                  </Link>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                {trainingLoading ? (
                  <div className="space-y-2"><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-3/4" /></div>
                ) : trainTotal === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No training requirements configured</p>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-muted-foreground">Compliance rate</span>
                      <span className="text-sm font-semibold tabular-nums">{trainRate}%</span>
                    </div>
                    <Progress value={trainRate} className="h-2" />
                    <div className="grid grid-cols-3 gap-2 pt-1">
                      {[
                        { label: "Compliant", value: trainCompliant, color: "text-emerald-600" },
                        { label: "Expiring",  value: trainExpiring,  color: "text-amber-600"  },
                        { label: "Issues",    value: trainIssues,    color: "text-red-600"    },
                      ].map(({ label, value, color }) => (
                        <div key={label} className="text-center rounded-md bg-muted/40 py-2 px-1">
                          <p className={`text-xl font-bold tabular-nums ${color}`}>{value}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
                        </div>
                      ))}
                    </div>
                    {trainByDept.length > 0 && (
                      <div className="pt-1 border-t border-border space-y-2">
                        <p className="text-xs font-medium text-muted-foreground">By department</p>
                        {trainByDept.slice(0, 5).map(d => {
                          const rate = d.total > 0 ? Math.round((d.compliant / d.total) * 100) : 0;
                          return (
                            <div key={d.dept} className="space-y-0.5">
                              <div className="flex justify-between text-xs">
                                <span className="text-muted-foreground truncate max-w-[130px]">{d.dept}</span>
                                <span className="font-medium tabular-nums shrink-0 ml-1">{rate}%</span>
                              </div>
                              <div className="h-1 rounded-full bg-muted overflow-hidden">
                                <div className="h-full rounded-full bg-blue-500/60" style={{ width: `${rate}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* ── Equipment Analytics ───────────────────────────────────────────── */}
        <Card>
          <CardHeader className="pb-3 border-b">
            <CardTitle className="text-base flex items-center gap-2">
              <div className="h-7 w-7 rounded-lg bg-slate-500/10 flex items-center justify-center shrink-0">
                <Laptop className="h-4 w-4 text-slate-600" />
              </div>
              Equipment Analytics
              <Link to="/equipment" className="ml-auto text-xs font-normal text-muted-foreground hover:text-foreground flex items-center gap-0.5 transition-colors">
                Manage <ChevronRight className="h-3 w-3" />
              </Link>
            </CardTitle>
            <CardDescription>Assignment status, category breakdown, and expiring items</CardDescription>
          </CardHeader>
          <CardContent className="pt-4 space-y-6">
            {equipLoading || stockLoading ? (
              <div className="grid grid-cols-3 gap-4">
                <Skeleton className="h-20 rounded-lg" /><Skeleton className="h-20 rounded-lg" /><Skeleton className="h-20 rounded-lg" />
              </div>
            ) : (
              <>
                {/* Top KPIs */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: "Total Items",  value: allEquipment.length,   color: "" },
                    { label: "Assigned",     value: equipAssigned,          color: "" },
                    { label: "Unassigned",   value: equipUnassigned,        color: equipUnassigned > 0 ? "text-amber-600" : "" },
                    { label: "Expiring (30d)", value: equipExpiring.length, color: equipExpiring.length > 0 ? "text-amber-600" : "" },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="rounded-lg bg-muted/50 p-4">
                      <p className={`text-2xl font-bold tabular-nums leading-none ${color}`}>{value}</p>
                      <p className="text-xs text-muted-foreground mt-1.5">{label}</p>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* By category */}
                  {equipByCategory.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">By Category</p>
                      {equipByCategory.slice(0, 8).map(cat => {
                        const pct = cat.total > 0 ? Math.round((cat.assigned / cat.total) * 100) : 0;
                        return (
                          <div key={cat.cat} className="space-y-0.5">
                            <div className="flex justify-between text-xs">
                              <span className="text-muted-foreground truncate max-w-[160px]">{cat.cat}</span>
                              <span className="text-muted-foreground tabular-nums shrink-0 ml-2">{cat.assigned}/{cat.total} assigned</span>
                            </div>
                            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                              <div className="h-full rounded-full bg-slate-400/70" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Stock overview */}
                  {equipmentStock.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Stock Overview</p>
                      <div className="rounded-md border overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-xs">Item</TableHead>
                              <TableHead className="text-xs text-right">Total</TableHead>
                              <TableHead className="text-xs text-right">Assigned</TableHead>
                              <TableHead className="text-xs text-right">Available</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {equipmentStock.slice(0, 8).map(s => (
                              <TableRow key={s.id}>
                                <TableCell className="text-xs font-medium">{s.name}</TableCell>
                                <TableCell className="text-xs text-right tabular-nums">{s.totalQuantity}</TableCell>
                                <TableCell className="text-xs text-right tabular-nums">{s.assignedQuantity}</TableCell>
                                <TableCell className={`text-xs text-right tabular-nums font-medium ${s.availableQuantity === 0 ? "text-amber-600" : "text-emerald-600"}`}>
                                  {s.availableQuantity}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </div>

                {/* Expiring items */}
                {equipExpiring.length > 0 && (
                  <div className="space-y-2 border-t border-border pt-4">
                    <p className="text-sm font-medium flex items-center gap-1.5">
                      <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                      Expiring within 30 days
                    </p>
                    <div className="rounded-md border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">Item</TableHead>
                            <TableHead className="text-xs">Employee</TableHead>
                            <TableHead className="text-xs">Category</TableHead>
                            <TableHead className="text-xs text-right">Expires</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {equipExpiring.slice(0, 10).map(item => (
                            <TableRow key={item.id}>
                              <TableCell className="text-xs font-medium">{item.itemName}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{item.employeeName || "—"}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{item.category || "—"}</TableCell>
                              <TableCell className="text-xs text-right font-medium text-amber-600">
                                {new Date(item.expiryDate!).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {allEquipment.length === 0 && (
                  <div className="py-8 text-center">
                    <Laptop className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No equipment tracked yet</p>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Children Summary */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Baby className="h-4 w-4 text-muted-foreground" />
              Children Summary
            </CardTitle>
            <CardDescription>Track employees' children for New Year gifts and benefits planning</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-end gap-3 no-print">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Min age</p>
                <Input
                  type="number"
                  min={0}
                  placeholder="0"
                  value={childrenMinAge}
                  onChange={(e) => setChildrenMinAge(e.target.value)}
                  className="w-20 h-8 text-sm"
                />
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Max age</p>
                <Input
                  type="number"
                  min={0}
                  placeholder="any"
                  value={childrenMaxAge}
                  onChange={(e) => setChildrenMaxAge(e.target.value)}
                  className="w-20 h-8 text-sm"
                />
              </div>
              <Button
                size="sm"
                variant="outline"
                className="h-8"
                disabled={childrenSummaryLoading}
                onClick={() => loadChildrenSummary(
                  childrenMinAge !== "" ? parseInt(childrenMinAge) : undefined,
                  childrenMaxAge !== "" ? parseInt(childrenMaxAge) : undefined,
                )}
              >
                <Search className="h-3.5 w-3.5 mr-1" /> Filter
              </Button>
              {(childrenMinAge || childrenMaxAge) && (
                <Button size="sm" variant="ghost" className="h-8 text-muted-foreground" onClick={() => { setChildrenMinAge(""); setChildrenMaxAge(""); loadChildrenSummary(); }}>
                  <X className="h-3.5 w-3.5 mr-1" /> Clear
                </Button>
              )}
            </div>

            {childrenSummary && (
              <>
                <div className="flex items-center gap-2 p-3 rounded-lg bg-pink-500/10 w-fit">
                  <Baby className="h-5 w-5 text-pink-600" />
                  <span className="text-sm font-semibold text-pink-600">{childrenSummary.totalChildren} children total</span>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {childrenSummary.byAgeGroup.map((g) => (
                    <div key={g.label} className="text-center p-3 rounded-lg border border-border bg-muted/30">
                      <p className="text-lg font-bold">{g.count}</p>
                      <p className="text-xs text-muted-foreground">{g.label} yrs</p>
                    </div>
                  ))}
                </div>
                {childrenSummary.children.length > 0 && (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Child</TableHead>
                          <TableHead>Date of Birth</TableHead>
                          <TableHead>Age</TableHead>
                          <TableHead>Employee</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {childrenSummary.children.map((c) => (
                          <TableRow key={c.id}>
                            <TableCell className="font-medium">{c.firstName}</TableCell>
                            <TableCell>{c.dateOfBirth}</TableCell>
                            <TableCell>{c.age} {c.age === 1 ? "yr" : "yrs"}</TableCell>
                            <TableCell className="text-muted-foreground">{c.employeeName}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </>
            )}
            {!childrenSummary && !childrenSummaryLoading && (
              <p className="text-sm text-muted-foreground italic">No data loaded yet.</p>
            )}
          </CardContent>
        </Card>

          </TabsContent>

          <TabsContent value="reports" className="mt-0">
            <ReportCanvas />
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
